import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, Building2, ShieldCheck, ArrowLeft, Trophy, Bell, Check } from "lucide-react";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import useSeoHead from "../hooks/useSeoHead";

// Central sport catalog (mirrors backend `sports` seed). Keep this small —
// if new sports are added they just won't get a friendly label; UI degrades.
const SPORTS = [
  { slug: "pickleball", name: "Pickleball" },
  { slug: "padel", name: "Padel" },
  { slug: "tennis", name: "Tennis" },
  { slug: "golf", name: "Golf" },
  { slug: "basketball", name: "Basketball" },
  { slug: "football", name: "Football" },
  { slug: "volleyball", name: "Volleyball" },
];

const SPORT_NAME = Object.fromEntries(SPORTS.map((s) => [s.slug, s.name]));

function titleCaseSlug(slug) {
  return (slug || "")
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export default function CityLanding() {
  const { city: citySlug, sport: sportSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | empty | error
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const params = sportSlug ? `?sport=${encodeURIComponent(sportSlug)}` : "";
      const { data: d } = await api.get(`/discovery/city/${citySlug}${params}`);
      setData(d);
      setStatus((d.total || 0) > 0 ? "ok" : "empty");
    } catch (e) {
      if (e.response?.status === 404) { setStatus("empty"); setData(null); return; }
      setErr(e.response?.data?.detail || e.message);
      setStatus("error");
    }
  }, [citySlug, sportSlug]);

  useEffect(() => { load(); }, [load]);

  // Canonical resolves to the slugged URL — even if the display name has
  // different casing/accents, crawlers always see a single canonical form.
  const canonical = typeof window !== "undefined"
    ? `${window.location.origin}/clubs/in/${citySlug}${sportSlug ? `/${sportSlug}` : ""}`
    : undefined;

  const cityDisplay = data?.city || titleCaseSlug(citySlug);
  const sportDisplay = sportSlug ? (SPORT_NAME[sportSlug] || titleCaseSlug(sportSlug)) : null;
  const pageTitle = sportDisplay
    ? `${sportDisplay} in ${cityDisplay} · Pick2Play`
    : `Play in ${cityDisplay} · Pick2Play`;
  const pageDescription = useMemo(() => {
    const nF = data?.facilities?.length || 0;
    const nC = data?.clubs?.length || 0;
    const parts = [];
    if (nC) parts.push(`${nC} club${nC === 1 ? "" : "s"}`);
    if (nF) parts.push(`${nF} verified venue${nF === 1 ? "" : "s"}`);
    const counts = parts.length ? parts.join(" and ") + " · " : "";
    return sportDisplay
      ? `${counts}Find ${sportDisplay} courts, matches, tournaments and coaches in ${cityDisplay}. Public directory on Pick2Play.`
      : `${counts}Find courts, clubs, matches, tournaments and coaches in ${cityDisplay}. Public directory on Pick2Play.`;
  }, [data, cityDisplay, sportDisplay]);

  // JSON-LD — `CollectionPage` with an `ItemList` so Google understands this
  // is an aggregator. Each item is a `SportsClub` or `SportsActivityLocation`
  // with an absolute URL so they're eligible for rich results.
  const jsonLd = useMemo(() => {
    if (!data) return null;
    const items = [];
    data.clubs?.forEach((c, i) => {
      items.push({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "SportsClub",
          name: c.name,
          url: `${typeof window !== "undefined" ? window.location.origin : ""}/clubs/${c.slug || c.id}`,
          ...(c.city ? { address: { "@type": "PostalAddress", addressLocality: c.city, addressCountry: c.country } } : {}),
        },
      });
    });
    const offset = data.clubs?.length || 0;
    data.facilities?.forEach((f, i) => {
      items.push({
        "@type": "ListItem",
        position: offset + i + 1,
        item: {
          "@type": "SportsActivityLocation",
          name: f.name,
          url: `${typeof window !== "undefined" ? window.location.origin : ""}/facilities/${f.slug || f.id}`,
          ...(f.city ? { address: { "@type": "PostalAddress", addressLocality: f.city, addressCountry: f.country } } : {}),
        },
      });
    });
    return {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: pageTitle,
      description: pageDescription,
      url: canonical,
      mainEntity: { "@type": "ItemList", numberOfItems: items.length, itemListElement: items },
    };
  }, [data, pageTitle, pageDescription, canonical]);

  useSeoHead({
    title: pageTitle,
    description: pageDescription,
    canonical,
    type: "website",
    jsonLd,
  });

  return (
    <AppLayout>
      <Link to="/clubs" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1 mb-4" data-testid="city-back">
        <ArrowLeft size={14} /> All clubs
      </Link>
      <h1 className="font-heading text-3xl sm:text-4xl font-black flex items-baseline flex-wrap gap-2" data-testid="city-heading">
        <span>{sportDisplay ? `${sportDisplay} in` : "Play in"}</span>
        <span className="text-[#00E676]">{cityDisplay}</span>
        {data?.country && <span className="text-lg text-zinc-500">· {data.country}</span>}
      </h1>
      <p className="text-zinc-400 mt-2 max-w-xl">{pageDescription}</p>

      {/* Sport filter pills — switches between `/clubs/in/{city}` variants */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="city-sport-pills">
        <button
          data-testid="city-sport-all"
          onClick={() => navigate(`/clubs/in/${citySlug}`)}
          className={`pp-chip ${!sportSlug ? "pp-chip-active" : ""}`}
        >
          All sports
        </button>
        {SPORTS.map((s) => (
          <button
            key={s.slug}
            data-testid={`city-sport-${s.slug}`}
            onClick={() => navigate(`/clubs/in/${citySlug}/${s.slug}`)}
            className={`pp-chip ${sportSlug === s.slug ? "pp-chip-active" : ""}`}
          >
            {s.name}
          </button>
        ))}
      </div>

      <WatchCityStrip
        citySlug={citySlug}
        cityDisplay={cityDisplay}
        sportSlug={sportSlug || null}
        sportDisplay={sportDisplay}
      />

      {status === "loading" && <div className="pp-card p-10 text-center text-zinc-500 mt-6">Loading…</div>}

      {status === "empty" && (
        <div className="pp-card-elevated p-10 text-center mt-8" data-testid="city-empty">
          <div className="font-heading text-2xl font-black mb-2">
            No {sportDisplay ? sportDisplay.toLowerCase() : "sports"} spots yet in {cityDisplay}
          </div>
          <p className="text-zinc-400 mb-6">Help us grow this city — add your venue or club and get listed here for free.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/clubs" className="pp-btn-primary px-5 py-3 rounded-xl">Browse other cities</Link>
            <Link to="/facilities" className="pp-btn-secondary px-5 py-3 rounded-xl">Search venues</Link>
          </div>
        </div>
      )}

      {status === "error" && (
        <div className="pp-card p-10 text-center text-[#FF3B30] mt-6" data-testid="city-error">{err}</div>
      )}

      {status === "ok" && data && (
        <>
          {data.clubs?.length > 0 && (
            <section className="mt-10" data-testid="city-clubs-section">
              <h2 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2">
                <Building2 size={18} className="text-[#00E676]" /> Clubs ({data.clubs.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.clubs.map((c) => (
                  <Link
                    key={c.id}
                    to={`/clubs/${c.slug || c.id}`}
                    data-testid={`city-club-${c.id}`}
                    className="pp-card p-4 hover:border-[#00E676]/40 transition-colors flex items-start gap-3"
                  >
                    {c.logo_url ? (
                      <img src={c.logo_url} alt="" className="h-12 w-12 rounded-lg object-cover border border-white/5 shrink-0" />
                    ) : (
                      <div className="h-12 w-12 rounded-lg bg-[#00E676]/10 flex items-center justify-center shrink-0"><Building2 size={18} className="text-[#00E676]" /></div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-medium flex items-center gap-1.5">
                        <span className="truncate">{c.name}</span>
                        {c.is_verified && <ShieldCheck size={12} className="text-[#00E676] shrink-0" />}
                        {c.is_private && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FFD60A]/10 text-[#FFD60A] uppercase">Private</span>}
                      </div>
                      {c.description && <div className="text-[11px] text-zinc-500 mt-1 line-clamp-2">{c.description}</div>}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {data.facilities?.length > 0 && (
            <section className="mt-10" data-testid="city-facilities-section">
              <h2 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2">
                <Trophy size={18} className="text-[#007AFF]" /> Venues ({data.facilities.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.facilities.map((f) => (
                  <Link
                    key={f.id}
                    to={`/facilities/${f.slug || f.id}`}
                    data-testid={`city-facility-${f.id}`}
                    className="pp-card p-4 hover:border-[#007AFF]/40 transition-colors"
                  >
                    <div className="font-medium flex items-center gap-1.5">
                      <span className="truncate">{f.name}</span>
                      {f.verified && <ShieldCheck size={12} className="text-[#00E676] shrink-0" />}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1 flex flex-wrap items-center gap-1.5">
                      <MapPin size={10} />
                      <span>{[f.city, f.country].filter(Boolean).join(", ")}</span>
                      {f.num_courts ? <span>· {f.num_courts} courts</span> : null}
                      {f.indoor === true ? <span>· Indoor</span> : f.indoor === false ? <span>· Outdoor</span> : null}
                    </div>
                    {f.sport_slugs?.length > 0 && (
                      <div className="mt-2 flex gap-1 flex-wrap">
                        {f.sport_slugs.slice(0, 4).map((s) => (
                          <span key={s} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-400 uppercase">{s}</span>
                        ))}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </AppLayout>
  );
}



// ─────────────────── WatchCityStrip ─────────────────────────────────────
// Inline email-capture strip for city landing pages. Posts to
// `POST /api/watch/city` and flips into a success state. Keeps the form
// small enough to live between the sport pills and the listing — never
// blocks scroll or pushes listings below the fold.
function WatchCityStrip({ citySlug, cityDisplay, sportSlug, sportDisplay }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Reset success state if the user switches city/sport variants.
  useEffect(() => { setDone(false); }, [citySlug, sportSlug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/watch/city", {
        email: email.trim(),
        city_slug: citySlug,
        city_display: cityDisplay,
        sport: sportSlug || null,
      });
      if (data.already) toast.info("You're already on this list — nothing to change.");
      else toast.success("Gotcha — check your inbox to confirm.");
      setDone(true);
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Couldn't save — try again.");
    } finally {
      setBusy(false);
    }
  };

  const label = sportDisplay
    ? `Get an email when a new ${sportDisplay.toLowerCase()} venue opens in ${cityDisplay}.`
    : `Get an email when a new venue opens in ${cityDisplay}.`;

  if (done) {
    return (
      <div
        className="mt-6 pp-card p-4 flex items-center gap-3 border-[#00E676]/30"
        data-testid="city-watch-success"
      >
        <Check size={18} className="text-[#00E676] shrink-0" />
        <div className="text-sm">
          <div className="font-medium">You're on the list for {cityDisplay}{sportDisplay ? ` · ${sportDisplay}` : ""}.</div>
          <div className="text-xs text-zinc-500 mt-0.5">One-click unsubscribe in every email. No spam — never more than once a week.</div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="mt-6 pp-card p-4 flex flex-col sm:flex-row sm:items-center gap-3"
      data-testid="city-watch-form"
    >
      <div className="flex items-start sm:items-center gap-2 flex-1">
        <Bell size={16} className="text-[#00E676] shrink-0 mt-0.5 sm:mt-0" />
        <div>
          <div className="text-sm font-medium">{label}</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">No spam — one email max per week. Unsubscribe any time.</div>
        </div>
      </div>
      <div className="flex gap-2">
        <input
          type="email"
          required
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="city-watch-email"
          className="pp-input text-sm flex-1 sm:w-56"
        />
        <button
          type="submit"
          disabled={busy || !email.trim()}
          data-testid="city-watch-submit"
          className="pp-btn-primary px-4 py-2 rounded-xl text-sm whitespace-nowrap disabled:opacity-40"
        >
          {busy ? "Saving…" : "Notify me"}
        </button>
      </div>
    </form>
  );
}
